import type { RuntimeBridge } from "../types/RuntimeBridge"
import type { WasmBinary } from "../utils/ResolveWasm"
import { CreateSingleton, LockManager, RecordMap } from "@pdfking/tfunc"
import { CreateWasmLoader } from "./CreateWasmLoader"
import { CreateWasmSession } from "./CreateWasmSession"

type T = number | bigint

type Locked<T> = T extends (...args: infer A) => infer R
  ? (...args: A) => Promise<R>
  : never

export type ProvisionAPI<F> = {
  [K in keyof F]: Locked<F[K]>
} & {
  readonly instance: RuntimeBridge<T>
}

export const CreateProvisionWasm = <F extends Record<string, any>>(
  binary: WasmBinary,
  createFunctions: (i: RuntimeBridge<T>) => F
) => {
  console.info("[provision] using WASM path")
  const loader = CreateSingleton(() => CreateWasmLoader(binary))

  const { schedule } = LockManager()

  const session = CreateWasmSession(async () => {
    const create = await loader()
    return create()
  })

  const createAPI = (instance: RuntimeBridge<T>): ProvisionAPI<F> => {
    const fns = createFunctions(instance)
    const api = RecordMap(
      (func: any) =>
        (...args: any[]) =>
          schedule("λ", async () => func(...args)),
      fns
    ) as unknown as Omit<ProvisionAPI<F>, "instance">

    return { instance, ...api } as ProvisionAPI<F>
  }

  return async function* ProvisionWasm<R>(
    fn: (api: ProvisionAPI<F>) => AsyncGenerator<R>
  ): AsyncGenerator<R> {
    yield* session(async function* (instance: RuntimeBridge<T>) {
      yield* fn(createAPI(instance))
    })
  }
}
